import { useCallback, useEffect, useRef, useState } from 'react';
import api from '../services/api';
import { AnsweredQuestionState, AttemptProgressResponse, TestAnswer, TestAttempt, TestStartResponse } from '../types';
import { getApiErrorCode, getApiErrorMessage } from '../utils/apiError';

export const TEST_REQUEST = { timeout: 15000 };
type Failure = { message: string; action: 'start' | 'save' | 'sync' | 'complete' | 'closed'; questionId?: number };
const statusOf = (error: any): number | undefined => error?.response?.status;
const messageOf = (error: unknown, fallback: string): string => {
  const status = statusOf(error);
  return status && [400, 403, 404, 409, 422].includes(status) ? getApiErrorMessage(error, fallback) : fallback;
};

/** Server progress is authoritative; a lost HTTP response never means a lost answer. */
export function useTestSession(testId: string | undefined) {
  const [testData, setTestData] = useState<TestStartResponse | null>(null);
  const dataRef = useRef<TestStartResponse | null>(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const indexRef = useRef(0);
  const [answers, setAnswers] = useState<Record<number, TestAnswer>>({});
  const answersRef = useRef<Record<number, TestAnswer>>({});
  const savedRef = useRef<Record<number, boolean>>({});
  const [answerResults, setAnswerResults] = useState<Record<number, boolean>>({});
  const [correctAnswerIds, setCorrectAnswerIds] = useState<Record<number, number[]>>({});
  const [correctMatching, setCorrectMatching] = useState<Record<number, Record<string, string>>>({});
  const [timeLeft, setTimeLeft] = useState(0);
  const deadlineRef = useRef(0);
  const questionStartedRef = useRef(0);
  const expiryHandledRef = useRef(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const busyRef = useRef(false);
  const scopeRef = useRef(0);
  const [completed, setCompleted] = useState(false);
  const completedRef = useRef(false);
  const [result, setResult] = useState<TestAttempt | null>(null);
  const [requestError, setRequestError] = useState<Failure | null>(null);
  const errorRef = useRef<Failure | null>(null);
  const [busyLabel, setBusyLabel] = useState('');

  const fail = useCallback((value: Failure | null) => { errorRef.current = value; setRequestError(value); }, []);
  const clock = useCallback((seconds: number) => {
    const remaining = Math.max(0, Number.isFinite(seconds) ? seconds : 0);
    deadlineRef.current = performance.now() + remaining * 1000;
    setTimeLeft(Math.ceil(remaining));
  }, []);
  const move = useCallback((index: number) => {
    indexRef.current = index; setCurrentQuestionIndex(index); questionStartedRef.current = performance.now();
  }, []);
  const applySaved = useCallback((states: AnsweredQuestionState[]) => {
    const feedback: Record<number, boolean> = {};
    const ids: Record<number, number[]> = {};
    const matching: Record<number, Record<string, string>> = {};
    for (const state of states) {
      if (typeof state.is_correct !== 'boolean') continue;
      const id = state.question_id;
      savedRef.current[id] = state.is_correct;
      feedback[id] = state.is_correct;
      ids[id] = state.correct_answer_ids ?? [];
      if (state.correct_matching) matching[id] = state.correct_matching;
      answersRef.current[id] = { question_id: id, answer_text: state.answer_text ?? undefined,
        selected_answer_ids: state.selected_answer_ids ?? [], matching_data: state.matching_data ?? undefined };
    }
    setAnswers({ ...answersRef.current });
    setAnswerResults((value) => ({ ...value, ...feedback }));
    setCorrectAnswerIds((value) => ({ ...value, ...ids }));
    setCorrectMatching((value) => ({ ...value, ...matching }));
    if (dataRef.current) {
      const byId = new Map(states.map((state) => [state.question_id, state]));
      dataRef.current = { ...dataRef.current, questions: dataRef.current.questions.map((question) => {
        const state = byId.get(question.id);
        return state ? { ...question, explanation: state.explanation,
          explanation_image_urls: state.explanation_image_urls, explanation_image_captions: state.explanation_image_captions } : question;
      }) };
      setTestData(dataRef.current);
    }
  }, []);
  const acceptResult = useCallback((value: TestAttempt) => {
    completedRef.current = true; setCompleted(true); setResult(value); fail(null);
  }, [fail]);

  const recover = useCallback(async (attemptId: number, scope: number) => {
    setBusyLabel('Проверяем сохранение на сервере…');
    const { data } = await api.get<AttemptProgressResponse>(`/tests/attempts/${attemptId}/progress`, TEST_REQUEST);
    if (scope !== scopeRef.current) return;
    if (data.attempt_id !== attemptId || !Array.isArray(data.answered)) throw new Error('Invalid progress');
    applySaved(data.answered); clock(data.remaining_seconds);
    if (data.is_completed) {
      const response = await api.get<TestAttempt>(`/tests/attempts/${attemptId}`, TEST_REQUEST);
      if (scope === scopeRef.current && response.data.is_completed) acceptResult(response.data);
    }
  }, [acceptResult, applySaved, clock]);

  const startTest = useCallback(async () => {
    const scope = ++scopeRef.current;
    busyRef.current = false; setSubmitting(false); setLoading(true); fail(null);
    dataRef.current = null; setTestData(null); completedRef.current = false; setCompleted(false); setResult(null);
    answersRef.current = {}; savedRef.current = {}; expiryHandledRef.current = false;
    setAnswers({}); setAnswerResults({}); setCorrectAnswerIds({}); setCorrectMatching({});
    try {
      const { data } = await api.post<TestStartResponse>(`/tests/${testId}/start`, undefined, TEST_REQUEST);
      if (scope !== scopeRef.current) return;
      if (!data.questions?.length || !Number.isFinite(data.remaining_seconds)) throw new Error('Invalid test');
      dataRef.current = data; setTestData(data); applySaved(data.answered ?? []); clock(data.remaining_seconds);
      const firstUnanswered = data.questions.findIndex((question) => savedRef.current[question.id] === undefined);
      move(firstUnanswered < 0 ? data.questions.length - 1 : firstUnanswered);
    } catch (error) {
      if (scope !== scopeRef.current) return;
      // /start finalizes an expired attempt. Show that result rather than a dead end.
      if (getApiErrorCode(error) === 'attempt_time_expired') {
        try {
          const response = await api.get<TestAttempt[]>('/results/my-attempts', { ...TEST_REQUEST, params: { test_id: Number(testId) } });
          const latest = response.data.find((attempt) => attempt.test_id === Number(testId) && attempt.is_completed);
          if (scope !== scopeRef.current) return;
          if (latest) { acceptResult(latest); return; }
        } catch { /* The error below offers a bounded, explicit retry. */ }
      }
      if (scope === scopeRef.current) fail({ action: statusOf(error) === 403 ? 'closed' : 'start',
        message: messageOf(error, 'Не удалось открыть тест. Проверьте соединение и повторите загрузку.') });
    } finally { if (scope === scopeRef.current) setLoading(false); }
  }, [testId, applySaved, clock, move, fail, acceptResult]);

  useEffect(() => { void startTest(); return () => { scopeRef.current += 1; }; }, [startTest]);
  const timerAttemptId = testData?.attempt_id;
  useEffect(() => {
    if (!timerAttemptId || completed) return;
    const tick = () => setTimeLeft(Math.max(0, Math.ceil((deadlineRef.current - performance.now()) / 1000)));
    const timer = window.setInterval(tick, 1000);
    document.addEventListener('visibilitychange', tick);
    return () => { window.clearInterval(timer); document.removeEventListener('visibilitychange', tick); };
  }, [timerAttemptId, completed]);

  const upsertAnswer = useCallback((id: number, next: TestAnswer) => {
    if (busyRef.current || errorRef.current?.action === 'sync' || errorRef.current?.action === 'closed'
        || savedRef.current[id] !== undefined || deadlineRef.current <= performance.now()) return;
    answersRef.current = { ...answersRef.current, [id]: next }; setAnswers(answersRef.current);
  }, []);

  const saveOne = useCallback(async (id: number, scope: number): Promise<boolean> => {
    const data = dataRef.current;
    const draft = answersRef.current[id];
    if (!data || !draft) return false;
    if (savedRef.current[id] !== undefined) return true;
    const payload = { ...draft, time_spent_seconds: Math.min(86400, Math.max(0, Math.floor((performance.now() - questionStartedRef.current) / 1000))) };
    setBusyLabel('Сохраняем ответ…');
    try {
      const response = await api.post<AnsweredQuestionState>(`/tests/attempts/${data.attempt_id}/answer`, payload, TEST_REQUEST);
      if (scope !== scopeRef.current) return false;
      if (typeof response.data.is_correct !== 'boolean') throw new Error('Missing confirmation');
      applySaved([{ ...response.data, ...payload }]); fail(null); return true;
    } catch (error) {
      if (scope !== scopeRef.current) return false;
      if (getApiErrorCode(error) === 'attempt_time_expired') { clock(0); return false; }
      if ([400, 422].includes(statusOf(error) ?? 0)) {
        fail({ action: 'save', questionId: id, message: messageOf(error, 'Не удалось сохранить ответ. Проверьте выбранный вариант.') });
        return false;
      }
      try {
        await recover(data.attempt_id, scope);
        if (scope !== scopeRef.current) return false;
        if (savedRef.current[id] !== undefined || completedRef.current) { fail(null); return true; }
        if ([403, 404].includes(statusOf(error) ?? 0)) {
          fail({ action: 'closed', message: messageOf(error, 'Эта попытка больше недоступна. Вернитесь в личный кабинет.') });
        } else {
          fail({ action: 'save', questionId: id, message: 'Ответ не сохранён. Ваш выбор остался на странице. Проверьте соединение и повторите отправку.' });
        }
      } catch (recoveryError) {
        if (scope !== scopeRef.current) return false;
        const closed = [403, 404].includes(statusOf(recoveryError) ?? 0);
        fail({ action: closed ? 'closed' : 'sync', questionId: id,
          message: closed ? 'Эта попытка закрыта или перезапущена администратором. Вернитесь в личный кабинет.'
            : 'Не удалось проверить, сохранён ли ответ. Ваш выбор остался на странице. Проверьте сохранение перед продолжением.' });
      }
      return false;
    }
  }, [applySaved, clock, fail, recover]);

  const completeTest = useCallback(async (expired = false) => {
    const data = dataRef.current;
    if (!data || busyRef.current || completedRef.current) return;
    const scope = scopeRef.current;
    busyRef.current = true; setSubmitting(true); fail(null);
    try {
      if (!expired) {
        for (const question of data.questions) {
          if (completedRef.current) return;
          if (answersRef.current[question.id] && savedRef.current[question.id] === undefined) {
            if (!await saveOne(question.id, scope)) return;
          }
        }
      }
      if (scope !== scopeRef.current || completedRef.current) return;
      setBusyLabel('Завершаем тест…');
      try {
        const response = await api.post<TestAttempt>(`/tests/attempts/${data.attempt_id}/complete`, undefined, TEST_REQUEST);
        if (scope === scopeRef.current) acceptResult(response.data);
      } catch (error) {
        // Completion may have committed while its response was lost. Never create
        // a new attempt or resubmit answers to find out whether it succeeded.
        const response = await api.get<TestAttempt>(`/tests/attempts/${data.attempt_id}`, TEST_REQUEST);
        if (scope !== scopeRef.current) return;
        if (response.data.is_completed) acceptResult(response.data);
        else fail({ action: 'complete', message: messageOf(error, 'Не удалось завершить тест. Сохранённые ответы на сервере остаются. Повторите завершение.') });
      }
    } catch (error) {
      if (scope === scopeRef.current) fail({ action: [403, 404].includes(statusOf(error) ?? 0) ? 'closed' : 'complete',
        message: messageOf(error, 'Не удалось проверить завершение теста. Сохранённые ответы остаются на сервере. Повторите проверку.') });
    } finally { if (scope === scopeRef.current) { busyRef.current = false; setSubmitting(false); setBusyLabel(''); } }
  }, [acceptResult, fail, saveOne]);

  useEffect(() => {
    if (testData && timeLeft === 0 && !loading && !submitting && !completed && !expiryHandledRef.current
        && errorRef.current?.action !== 'closed' && errorRef.current?.action !== 'sync') {
      expiryHandledRef.current = true; void completeTest(true);
    }
  }, [testData, timeLeft, loading, submitting, completed, completeTest]);

  const handleNext = useCallback(async () => {
    const data = dataRef.current;
    if (!data || busyRef.current || completedRef.current || errorRef.current?.action === 'sync'
        || errorRef.current?.action === 'closed') return;
    if (deadlineRef.current <= performance.now()) { clock(0); return; }
    const question = data.questions[indexRef.current];
    if (savedRef.current[question.id] === undefined) {
      if (!answersRef.current[question.id]) return;
      const scope = scopeRef.current;
      busyRef.current = true; setSubmitting(true); fail(null);
      try { await saveOne(question.id, scope); }
      finally { if (scope === scopeRef.current) { busyRef.current = false; setSubmitting(false); setBusyLabel(''); } }
    } else if (indexRef.current < data.questions.length - 1) move(indexRef.current + 1);
    else await completeTest();
  }, [clock, completeTest, fail, move, saveOne]);
  const handlePrevious = useCallback(() => {
    if (!busyRef.current && errorRef.current?.action !== 'sync' && indexRef.current > 0) move(indexRef.current - 1);
  }, [move]);
  const retryRequest = useCallback(async () => {
    const error = errorRef.current;
    if (!error || busyRef.current) return;
    if (error.action === 'start') { await startTest(); return; }
    if (error.action === 'complete') { await completeTest(deadlineRef.current <= performance.now()); return; }
    const data = dataRef.current;
    if (!data || error.action === 'closed') return;
    const scope = scopeRef.current;
    busyRef.current = true; setSubmitting(true);
    try {
      if (error.action === 'save' && error.questionId) await saveOne(error.questionId, scope);
      else {
        await recover(data.attempt_id, scope);
        if (scope === scopeRef.current) fail(null);
      }
    } catch {
      if (scope === scopeRef.current) fail({ ...error, message: 'Связь с сервером не восстановлена. Ваши сохранённые ответы не потеряны. Попробуйте ещё раз.' });
    } finally { if (scope === scopeRef.current) { busyRef.current = false; setSubmitting(false); setBusyLabel(''); } }
  }, [completeTest, fail, recover, saveOne, startTest]);

  useEffect(() => {
    const refresh = async () => {
      const data = dataRef.current;
      if (document.visibilityState === 'hidden' || !data || busyRef.current || completedRef.current) return;
      const scope = scopeRef.current;
      busyRef.current = true; setSubmitting(true);
      try {
        await recover(data.attempt_id, scope);
        if (scope === scopeRef.current) fail(null);
      } catch (error) {
        if (scope === scopeRef.current) fail({ action: [403, 404].includes(statusOf(error) ?? 0) ? 'closed' : 'sync',
          message: 'Не удалось обновить состояние попытки. Проверьте сохранение перед продолжением.' });
      } finally { if (scope === scopeRef.current) { busyRef.current = false; setSubmitting(false); setBusyLabel(''); } }
    };
    const visible = () => { void refresh(); };
    window.addEventListener('focus', visible);
    document.addEventListener('visibilitychange', visible);
    return () => { window.removeEventListener('focus', visible); document.removeEventListener('visibilitychange', visible); };
  }, [fail, recover]);

  return { testData, currentQuestionIndex, answers, answerResults, correctAnswerIds, correctMatching,
    timeLeft, loading, submitting, completed, result, requestError, busyLabel,
    inputLocked: submitting || timeLeft === 0 || requestError?.action === 'sync' || requestError?.action === 'closed',
    upsertAnswer, handleNext, handlePrevious, retryRequest };
}
